"use client";

import { useEffect, useRef, type ReactNode } from "react";

const callbacks = new Map<Element, () => void>();
let observer: IntersectionObserver | null = null;

function getObserver() {
  if (observer || typeof window === "undefined" || !("IntersectionObserver" in window)) {
    return observer;
  }
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        callbacks.get(entry.target)?.();
        callbacks.delete(entry.target);
        observer?.unobserve(entry.target);
      });
    },
    { rootMargin: "0px 0px -12% 0px", threshold: 0.22 },
  );
  return observer;
}

export function MotionFigure({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = ref.current;
    node?.classList.add("academy-motion-ready");
    const io = getObserver();
    if (!node || !io) {
      node?.classList.add("is-visible");
      return;
    }
    callbacks.set(node, () => node.classList.add("is-visible"));
    io.observe(node);
    return () => {
      callbacks.delete(node);
      io.unobserve(node);
      node.classList.remove("academy-motion-ready", "is-visible");
    };
  }, []);

  // The reveal CSS lives once in globals.css (.academy-motion / .academy-*);
  // this wrapper only toggles the .academy-motion-ready / .is-visible classes.
  return (
    <div
      ref={ref}
      className={[
        "academy-motion",
        className ?? "",
      ].join(" ")}
    >
      {children}
    </div>
  );
}
